// WattelEM API Client - Simple HTTP only (no WebSocket)
import { translate } from '../i18n';

const BASE_URL = import.meta.env.VITE_WATTELZCE_API_URL || '';

// ============================================
// Types
// ============================================

export interface DeviceTelemetry {
  voltage_v: string | null;
  current_a: string | null;
  power_kw: string | null;
  power_factor: string | null;
  frequency_hz: string | null;
  energy_kwh: string | null;
  temperature_c: string | null;
  relay_state: number | null;
  recorded_at?: string;
}

export interface ZoneEquipment {
  id: number;
  name: string;
  icon: string;
  type: string | null;
  power_w: number | null;
  meter_power_w: number | null;
  meter_energy_wh: number | null;
  meter_timestamp: string | null;
  deduced_energy_wh: number | null;
}

export interface DeviceZone {
  zone_id: number;
  zone_name: string;
  zone_icon: string;
  installation_id: number;
  installation_name: string;
  equipment: ZoneEquipment[];
}

export interface DeviceOverview {
  device_sn: string;
  online: boolean;
  last_seen: string | null;
  mqtt_status: string | null;
  wifi_rssi: number | null;
  relay_state: number | null;
  relay_cmd: number | null;
  current: DeviceTelemetry;
  zones: DeviceZone[];
}

export interface HistoryPoint {
  voltage_v: string | null;
  current_a: string | null;
  power_kw: string | null;
  power_factor: string | null;
  frequency_hz: string | null;
  relay_state: number | null;
  energy_kwh: string | null;
  temperature_c: string | null;
  recorded_at: string;
}

export interface DailyEnergy {
  day: string;
  daily_kwh: number;
  avg_power_kw: number;
  max_power_kw: number;
}

// Backward-compatible type alias used by other components
export type TelemetryData = DeviceTelemetry & { device_sn?: string };

// ============================================
// Auth
// ============================================

function getHeaders(): HeadersInit {
  const token = localStorage.getItem('dashboard_zce_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  // L'appelant peut fournir son propre signal (annulation au démontage) : on le
  // chaîne sur le contrôleur interne pour garder aussi le timeout de 15s.
  const external = options?.signal;
  const onExternalAbort = () => controller.abort();
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', onExternalAbort, { once: true });
  }
  const cleanup = () => {
    clearTimeout(timeout);
    external?.removeEventListener('abort', onExternalAbort);
  };
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      signal: controller.signal,
      headers: { ...getHeaders(), ...(options?.headers || {}) },
    });
  } catch (err) {
    cleanup();
    if ((err as Error).name === 'AbortError') {
      // Annulation volontaire par l'appelant : ce n'est pas une erreur réseau.
      if (external?.aborted) throw err;
      throw new Error(translate('eq_requestTimeout'));
    }
    throw err;
  }
  cleanup();
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const errMsg = typeof body.error === 'string'
      ? body.error
      : body.error?.message || body.message || `HTTP ${res.status}`;
    throw new Error(errMsg);
  }
  const json = await res.json();
  return json.data ?? json;
}

// ============================================
// API
// ============================================

export const wattelEMApi = {
  getOverview: (signal?: AbortSignal): Promise<DeviceOverview[]> =>
    apiFetch('/api/wattel-em/overview', { signal }),

  getHistory: (sn: string, hours = 1, limit = 200): Promise<HistoryPoint[]> =>
    apiFetch(`/api/wattel-em/devices/${encodeURIComponent(sn)}/history?hours=${hours}&limit=${limit}`),

  getDailyEnergy: (sn: string, days = 30, startDate?: string, endDate?: string): Promise<DailyEnergy[]> => {
    const params = new URLSearchParams();
    if (startDate && endDate) {
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    } else {
      params.set('days', String(days));
    }
    return apiFetch(`/api/wattel-em/devices/${encodeURIComponent(sn)}/daily-energy?${params}`);
  },

  getHourlyEnergy: (sn: string, hours = 24): Promise<DailyEnergy[]> =>
    apiFetch(`/api/wattel-em/devices/${encodeURIComponent(sn)}/hourly-energy?hours=${hours}`),

  /**
   * source='schedule' : commande émise par l'exécuteur de planning. Le serveur
   * l'accepte quel que soit le rôle, à condition qu'elle corresponde au planning
   * actif en base. Sans source, c'est une commande manuelle soumise aux droits.
   */
  setRelay: (sn: string, value: 0 | 1, source?: 'schedule') =>
    apiFetch(`/api/wattel-em/devices/${encodeURIComponent(sn)}/relay`, {
      method: 'POST',
      body: JSON.stringify(source ? { value, source } : { value }),
    }),

  // ── Mode de pilotage (« pilotable » / « compteur seul ») ──
  getControlSettings: (): Promise<ControlSettings> =>
    apiFetch('/api/wattel-em/control-settings'),

  setControlMode: (sn: string, mode: ControlMode): Promise<{ device_sn: string; mode: ControlMode; scheduleDisabled: boolean }> =>
    apiFetch(`/api/wattel-em/devices/${encodeURIComponent(sn)}/control-mode`, {
      method: 'PUT',
      body: JSON.stringify({ mode }),
    }),

  // ── Schedules (planification hebdomadaire ON/OFF) ──
  listSchedules: (): Promise<ScheduleRecord[]> =>
    apiFetch('/api/wattel-em/schedules'),

  getSchedule: (sn: string): Promise<ScheduleRecord | null> =>
    apiFetch(`/api/wattel-em/schedules/${encodeURIComponent(sn)}`),

  saveSchedule: (sn: string, body: ScheduleSavePayload): Promise<ScheduleRecord> =>
    apiFetch(`/api/wattel-em/schedules/${encodeURIComponent(sn)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteSchedule: (sn: string): Promise<void> =>
    apiFetch(`/api/wattel-em/schedules/${encodeURIComponent(sn)}`, { method: 'DELETE' }),

  health: () => apiFetch('/api/wattel-em/health'),
};

// Mode de pilotage d'un module et niveau de droits de l'utilisateur, tels que
// calculés par le serveur (source de vérité).
export type ControlMode = 'controllable' | 'meter_only';
export type ControlLevel = 'admin' | 'operator' | 'none';
export interface ControlSettings {
  level: ControlLevel;
  meterOnly: string[];
}

// Schedule API types (mirror SchedulePlanner.tsx DeviceSchedule)
export interface ScheduleSavePayload {
  enabled: boolean;
  name: string;
  weekly: Record<string, Array<{ start: string; end: string }>>;
  overrides?: Array<{ date: string; mode: 'all-off' | 'all-on' | 'custom'; slots?: Array<{ start: string; end: string }>; note?: string }>;
}
export interface ScheduleRecord extends ScheduleSavePayload {
  device_sn: string;
  updatedAt: string;
}

export default wattelEMApi;
